/* eslint-disable react/prop-types */
import { useMemo, useState } from "react";
import axios from "axios";
import { PackageCheck, Send, Truck } from "lucide-react";
import { StatusBadge } from "../../../../components/ui/index.js";
import { askForName, showErrorAlert } from "../../../../components/HelperAlerts/HelpersAlerts.jsx";
import { FULFILLMENT_TONE } from "./AdminOrders.jsx";

const PHYSICAL_TYPES = new Set(["product", "custom_order"]);
const SHIPPABLE_STATUSES = new Set(["pendiente_preparacion", "pendiente_produccion"]);

// Destinos válidos al resolver una incidencia, por tipo de línea — reflejo de
// `fulfillment._physical_transitions()`. El backend es la autoridad real:
// esto solo evita ofrecer una opción que el servidor rechazaría de todos modos.
const INCIDENT_RESOLUTION_TARGETS = {
    product: [
        ["pendiente_preparacion", "Volver a preparación"],
        ["enviado", "Ya está enviado"],
        ["cancelado", "Cancelar la línea"],
    ],
    custom_order: [
        ["pendiente_produccion", "Volver a producción"],
        ["enviado", "Ya está enviado"],
        ["cancelado", "Cancelar la línea"],
    ],
};

const formatApiError = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    return Object.entries(data)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(", ") : value}`)
        .join(" | ");
};

async function askReason(title) {
    const value = await askForName({
        title,
        text: "Este motivo queda registrado en el historial de la línea.",
        confirmButtonText: "Confirmar",
        cancelButtonText: "Cancelar",
    });
    return value || null;
}

export default function FulfillmentPanel({ order, onOrderUpdated }) {
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [trackingForm, setTrackingForm] = useState({ carrier: "correos", service_code: "", tracking_code: "", notes: "" });
    const [showShipForm, setShowShipForm] = useState(false);

    const physicalItems = useMemo(
        () => (order?.items || []).filter((item) => PHYSICAL_TYPES.has(item.item_type)),
        [order],
    );

    if (!order || physicalItems.length === 0) {
        return null; // digital-only: no hay fulfillment físico que operar aquí
    }

    const shippable = physicalItems.filter((item) => SHIPPABLE_STATUSES.has(item.fulfillment_status));
    const deliverable = physicalItems.filter((item) => item.fulfillment_status === "enviado");

    const toggleSelected = (id) => {
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const runAction = async (fn, fallbackMessage) => {
        setBusy(true);
        try {
            const { data } = await fn();
            onOrderUpdated?.(data);
            setSelected(new Set());
        } catch (err) {
            console.error("Fulfillment action failed:", err);
            showErrorAlert({ title: "No se pudo completar la acción", text: formatApiError(err, fallbackMessage) });
        } finally {
            setBusy(false);
        }
    };

    const handleShip = () => {
        const ids = [...selected].filter((id) => shippable.some((item) => item.id === id));
        if (ids.length === 0) return;
        runAction(
            () => axios.post(`/orders-admin/${order.order_number}/items/ship/`, {
                order_item_ids: ids, ...trackingForm,
            }),
            "No se pudo marcar el envío.",
        );
        setShowShipForm(false);
    };

    const handleDeliver = () => {
        const ids = [...selected].filter((id) => deliverable.some((item) => item.id === id));
        if (ids.length === 0) return;
        runAction(
            () => axios.post(`/orders-admin/${order.order_number}/items/deliver/`, { order_item_ids: ids }),
            "No se pudo marcar la entrega.",
        );
    };

    const handleIncident = async (item) => {
        const reason = await askReason(`Incidencia en "${item.snapshot_name}"`);
        if (!reason) return;
        runAction(
            () => axios.post(`/orders-admin/${order.order_number}/items/${item.id}/incident/`, { reason }),
            "No se pudo abrir la incidencia.",
        );
    };

    const handleResolve = async (item, toStatus) => {
        const reason = await askReason("Motivo de la resolución");
        if (!reason) return;
        runAction(
            () => axios.post(`/orders-admin/${order.order_number}/items/${item.id}/incident/resolve/`, {
                to_status: toStatus, reason,
            }),
            "No se pudo resolver la incidencia.",
        );
    };

    const handleCancel = async (item) => {
        const reason = await askReason(`Cancelar "${item.snapshot_name}"`);
        if (!reason) return;
        runAction(
            () => axios.post(`/orders-admin/${order.order_number}/items/${item.id}/cancel/`, { reason }),
            "No se pudo cancelar la línea.",
        );
    };

    const shipment = order.shipment_detail;

    return (
        <section className="rounded-lg border border-base-300 bg-base-100/70 p-3 flex flex-col gap-3">
            <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-primary" />
                <h4 className="font-semibold">Fulfillment físico</h4>
            </div>

            {shipment && (shipment.carrier || shipment.tracking_code) && (
                <div className="rounded-lg bg-base-200/60 p-2 text-xs flex flex-col gap-0.5">
                    <p><span className="text-base-content/55">Transportista:</span> {shipment.carrier || "—"} {shipment.service_code}</p>
                    <p><span className="text-base-content/55">Localizador:</span> <span className="font-mono">{shipment.tracking_code || "—"}</span></p>
                    {shipment.real_cost && (
                        <p><span className="text-base-content/55">Coste real:</span> {shipment.real_cost} {shipment.real_cost_currency}</p>
                    )}
                </div>
            )}

            <ul className="flex flex-col gap-2">
                {physicalItems.map((item) => {
                    const isShippable = SHIPPABLE_STATUSES.has(item.fulfillment_status);
                    const isDeliverable = item.fulfillment_status === "enviado";
                    const isIncident = item.fulfillment_status === "incidencia";
                    const isTerminal = item.fulfillment_status === "entregado" || item.fulfillment_status === "cancelado";
                    const canSelect = isShippable || isDeliverable;

                    return (
                        <li key={item.id} className="rounded-lg border border-base-300 p-2 flex flex-col gap-1.5">
                            <div className="flex items-center justify-between gap-2">
                                <label className="flex items-center gap-2 min-w-0 flex-1">
                                    {canSelect && (
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm checkbox-primary"
                                            checked={selected.has(item.id)}
                                            onChange={() => toggleSelected(item.id)}
                                            aria-label={`Seleccionar línea ${item.snapshot_name}`}
                                        />
                                    )}
                                    <span className="truncate text-sm font-medium">{item.snapshot_name}</span>
                                    <StatusBadge
                                        label={item.fulfillment_status_display || item.fulfillment_status}
                                        tone={FULFILLMENT_TONE[item.fulfillment_status] || "neutral"}
                                        size="xs"
                                    />
                                </label>
                                {!isTerminal && (
                                    <div className="flex items-center gap-1 shrink-0">
                                        {!isIncident && (
                                            <button
                                                type="button" className="btn btn-2xs btn-ghost text-warning"
                                                disabled={busy} onClick={() => handleIncident(item)}
                                            >
                                                Incidencia
                                            </button>
                                        )}
                                        {!isIncident && (
                                            <button
                                                type="button" className="btn btn-2xs btn-ghost text-error"
                                                disabled={busy} onClick={() => handleCancel(item)}
                                            >
                                                Cancelar
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {isIncident && (
                                <div className="flex flex-wrap gap-1 pl-6">
                                    {(INCIDENT_RESOLUTION_TARGETS[item.item_type] || []).map(([value, label]) => (
                                        <button
                                            key={value} type="button" disabled={busy}
                                            className="btn btn-2xs btn-outline"
                                            onClick={() => handleResolve(item, value)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            <div className="flex flex-wrap gap-2">
                {shippable.length > 0 && (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary gap-1"
                        disabled={busy || [...selected].filter((id) => shippable.some((i) => i.id === id)).length === 0}
                        onClick={() => setShowShipForm((v) => !v)}
                    >
                        <Send className="w-3.5 h-3.5" />
                        Marcar enviado
                    </button>
                )}
                {deliverable.length > 0 && (
                    <button
                        type="button"
                        className="btn btn-sm btn-success gap-1"
                        disabled={busy || [...selected].filter((id) => deliverable.some((i) => i.id === id)).length === 0}
                        onClick={handleDeliver}
                    >
                        <PackageCheck className="w-3.5 h-3.5" />
                        Marcar entregado
                    </button>
                )}
            </div>

            {showShipForm && (
                <div className="rounded-lg bg-base-200/60 p-3 flex flex-col gap-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <label className="form-control gap-1">
                            <span className="label-text text-xs">Transportista</span>
                            <input
                                className="input input-bordered input-sm"
                                value={trackingForm.carrier}
                                onChange={(e) => setTrackingForm((f) => ({ ...f, carrier: e.target.value }))}
                            />
                        </label>
                        <label className="form-control gap-1">
                            <span className="label-text text-xs">Servicio</span>
                            <input
                                className="input input-bordered input-sm"
                                value={trackingForm.service_code}
                                onChange={(e) => setTrackingForm((f) => ({ ...f, service_code: e.target.value }))}
                            />
                        </label>
                        <label className="form-control gap-1 sm:col-span-2">
                            <span className="label-text text-xs">Localizador</span>
                            <input
                                className="input input-bordered input-sm"
                                value={trackingForm.tracking_code}
                                onChange={(e) => setTrackingForm((f) => ({ ...f, tracking_code: e.target.value }))}
                                placeholder="Código de seguimiento de Correos"
                            />
                        </label>
                    </div>
                    <div className="flex justify-end gap-2">
                        <button type="button" className="btn btn-xs btn-ghost" onClick={() => setShowShipForm(false)} disabled={busy}>
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-xs btn-primary" onClick={handleShip} disabled={busy}>
                            Confirmar envío
                        </button>
                    </div>
                </div>
            )}

            {busy && (
                <div className="flex items-center gap-2 text-xs text-base-content/60">
                    <span className="loading loading-spinner loading-xs" /> Guardando…
                </div>
            )}
        </section>
    );
}
